import { useState } from 'react';
import { formatNumber, formatClockTime, type RoutePlan } from '@boatnav/core';
import { computeRouteWeather, type RouteWeatherSummary } from './routeWeather.js';
import type { Waypoint } from '../../lib/db.js';
import { msg } from '../../lib/messages.js';

/**
 * Lazy route forecast (spec 11) — button-driven, never an automatic fetch,
 * so the offline-first proxy budget isn't spent unless the skipper asks.
 * Shown under the leg table once a route (≥ 2 waypoints) and a boat exist.
 */
export function RouteWeatherPanel({ waypoints, plan }: { waypoints: Waypoint[]; plan: RoutePlan }): JSX.Element {
  const [summary, setSummary] = useState<RouteWeatherSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchWeather = async (): Promise<void> => {
    setLoading(true);
    try {
      setSummary(await computeRouteWeather(waypoints, plan, Date.now()));
    } finally {
      setLoading(false);
    }
  };

  if (!summary) {
    return (
      <button
        type="button"
        className="btn btn--ghost planner__weather-btn"
        disabled={loading}
        onClick={() => void fetchWeather()}
      >
        {loading ? msg.weather.fetchingRoute : msg.weather.fetchRoute}
      </button>
    );
  }

  return (
    <div className="planner__weather">
      <button type="button" className="planner__weather-line" onClick={() => setExpanded((e) => !e)}>
        {msg.weather.alongRoute}:{' '}
        {summary.minWindMs != null && summary.maxWindMs != null ? (
          <span className="numeral">
            {formatNumber(summary.minWindMs, 0)}–{formatNumber(summary.maxWindMs, 0)} m/s {summary.dominantLabel}
          </span>
        ) : (
          '—'
        )}
      </button>
      {expanded && (
        <ul className="planner__weather-samples">
          {summary.samples.map((s) => (
            <li key={s.index} className="numeral">
              #{s.index + 1} · {formatClockTime(s.etaMs)} ·{' '}
              {s.windMs != null ? `${formatNumber(s.windMs, 0)} m/s` : '—'}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
